import { SaveButton, useForm } from "@refinedev/antd";
import { Typography, Card, notification } from "antd";
import { useTranslate } from "@refinedev/core";
import { CheckOutlined, DownOutlined, RightOutlined } from "@ant-design/icons";
import { Flex, Button, theme } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { axiosInstance } from "@/utilities";
import { FormVerticalImportsProducts } from "@/components/vertical-form/importsProductsForm";
import { API_URL_LINKED_IMPORT_CONFIG } from "@/services/XCONS_ENV";
import { ProductImportConfiguration } from "@/types/schema/types";
import { UpdateButton } from "@/components";
import {
  ACTIVE_STATUSES,
  HistorialDeImportaciones,
  filasDe,
} from "./historialDeImportaciones";

const { Title, Text } = Typography;

interface ImportProductsProps {
  closeModal?: () => void;
  configXlsData: any;
  isLoading?: boolean;
  dataEmpty: boolean;
  sellerCode: string;
  tablePropsXlsTable: any;
  dataXlsTable: any;
  refetchTableXls?: any;
  refetchFormXls?: any;
  /** El link del aviso de coincidencias de un import (xcons-docs#624). */
  onAsociarCoincidencias?: (importId: number) => void;
}


export const ImportProducts: React.FC<ImportProductsProps> = ({
  closeModal,
  configXlsData,
  isLoading = false,
  dataEmpty,
  sellerCode,
  tablePropsXlsTable,
  dataXlsTable,
  refetchTableXls,
  refetchFormXls,
  onAsociarCoincidencias,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  /**
   * Subir un archivo pide una configuración **guardada**: es la que dice qué columna es cada
   * dato, y lo dice que exista la configuración, no que el form esté plegado.
   */
  const [formOk, setFormOk] = useState<boolean>(
    !!configXlsData?.data && Object.keys(configXlsData.data).length > 0,
  );
  /**
   * El form va plegado (xcons-docs#630): cerrado se ven sólo el título y el botón de cargar
   * Excel. Arranca abierto si todavía no hay configuración, porque sin ella no se puede subir.
   */
  const [formAbierto, setFormAbierto] = useState<boolean>(!formOk);
  const [enableProducts, setEnableProducts] = useState<boolean | undefined>(
    undefined,
  );

  const { formProps: formXlsProps, saveButtonProps: saveButtonXlsProps } =
    useForm({
      resource: "sellers/linkedproducts/import/configuration",
      id: sellerCode,
      onMutationSuccess: () => {
        notification.success({ message: t("message.configurationSucccess") });
        setFormOk(true);
      },
      onMutationError: (error) => {
        notification.error({
          message: t("message.configurationFailed"),
          description: error.message,
        });
      },
    });

  const handleSave = async (values: ProductImportConfiguration) => {
    const transformedValues = {
      sellerCode: sellerCode,
      firstLine: values.firstLine,
      sku: values.sku,
      name: values.name,
      brand: values.brand,
      price: values.price,
      costPrice: values.costPrice,
      taxes: values.taxes ?? false,
      specialPrice: values.specialPrice,
      specialPriceFrom: values.specialPriceFrom,
      specialPriceTo: values.specialPriceTo,
      dateFormat: values.dateFormat,
      stock: values.stock,
      leadTime: values.leadTime,
      featured: values.featured,
      manufacturerSku: values.manufacturerSku,
      enabled: values.enabled,
      customColumns: values.customColumns ?? [],
    };
    try {
      if (configXlsData && Object.keys(configXlsData.data).length > 0) {
        await axiosInstance.patch(
          `${API_URL_LINKED_IMPORT_CONFIG}/${sellerCode}`,
          transformedValues,
        );
        notification.success({ message: t("message.configurationUpdated") });
        refetchFormXls();
      } else {
        await axiosInstance.post(
          `${API_URL_LINKED_IMPORT_CONFIG}`,
          transformedValues,
        );
        notification.success({ message: t("message.configurationCreated") });
        refetchFormXls();
      }
      setFormOk(true);
    } catch {
      notification.error({ message: t("message.configurationErrorUpdating") });
    }
  };

  const cargarConfiguracionGuardada = () => {
    if (configXlsData?.data && Object.keys(configXlsData.data).length > 0) {
      formXlsProps.form?.setFieldsValue(configXlsData.data);
    } else {
      formXlsProps.form?.resetFields();
    }
  };

  useEffect(() => {
    cargarConfiguracionGuardada();
    // El aviso de coincidencias se cuenta al pedirlo (xcons-docs#626): lo que se asoció
    // mientras el modal estaba cerrado ya no tiene que figurar. El drawer se destruye al
    // cerrarse, así que esto corre en cada apertura.
    refetchTableXls?.();
  }, []);

  /** Mientras un Excel se procesa no se sube otro. */
  const hayExcelEnProceso = filasDe(dataXlsTable, "excel").some((r) =>
    ACTIVE_STATUSES.includes(r.status),
  );

  /**
   * El Excel que se está subiendo: hasta que la tabla trae su fila, se muestra una en proceso
   * en su lugar (xcons-docs#609).
   */
  const [subiendo, setSubiendo] = useState<{
    name: string;
    createdAt: string;
  } | null>(null);

  /** Al subir un Excel, la tabla viene a la vista: ahí aparece la importación en proceso. */
  const tablaRef = useRef<HTMLDivElement>(null);
  const irALaTabla = () =>
    setTimeout(
      () =>
        tablaRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      150,
    );

  return (
    // Como los demás paneles: el contenido scrollea y Cancelar / Guardar quedan fijos abajo.
    <Flex vertical style={{ flex: 1, overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
        <Card
          loading={isLoading}
          style={{
            borderStyle: "dashed",
            backgroundColor: token.colorFillAlter,
          }}
        >
          <Flex justify="space-between" align="center" gap={16}>
            <Flex
              align="center"
              gap={8}
              style={{ cursor: "pointer" }}
              onClick={() => setFormAbierto((a) => !a)}
            >
              {formAbierto ? (
                <DownOutlined style={{ color: token.colorTextSecondary }} />
              ) : (
                <RightOutlined style={{ color: token.colorTextSecondary }} />
              )}
              <Title
                level={5}
                style={{ color: token.colorTextSecondary, margin: 0 }}
              >
                {t("products.import.form.configTitle")}
              </Title>
            </Flex>
            <UpdateButton
              refetchTableXls={refetchTableXls}
              formOk={formOk}
              translate={t}
              configXlsData={configXlsData}
              sellerCode={sellerCode}
              enableProducts={enableProducts}
              disabled={hayExcelEnProceso || !!subiendo}
              onEmpezo={(name) => {
                setSubiendo({ name, createdAt: new Date().toISOString() });
                irALaTabla();
              }}
              onTermino={(ok) => {
                // Bien: la fila real reemplaza a la en proceso cuando llega con el refresco. Si
                // no llega, o si falló, la en proceso se va igual.
                const limpiar = () => setSubiendo(null);
                if (ok) setTimeout(limpiar, 2000);
                else limpiar();
              }}
            />
          </Flex>
          {/* Plegado se oculta sin desmontarse: el form no pierde lo que se estaba editando. */}
          <div style={{ display: formAbierto ? "block" : "none", marginTop: 24 }}>
            {/* La instrucción va con el form que explica, no como título (xcons-docs#634). */}
            <Text
              type="secondary"
              style={{ display: "block", marginBottom: 16 }}
            >
              {t("products.import.form.completeTitle")}
            </Text>
            <FormVerticalImportsProducts
              formXlsProps={formXlsProps}
              handleSave={handleSave}
            />
          </div>
        </Card>

        <HistorialDeImportaciones
          ref={tablaRef}
          origen="excel"
          datos={dataXlsTable}
          tableProps={tablePropsXlsTable}
          refetch={refetchTableXls}
          subiendo={subiendo}
          onLlegoLaNueva={() => setSubiendo(null)}
          onAsociarCoincidencias={onAsociarCoincidencias}
        />
      </div>
      {/* Cancelar y Guardar son del form: plegado no hay nada que guardar. */}
      <Flex
        align="center"
        justify="end"
        gap="8px"
        style={{
          display: formAbierto ? "flex" : "none",
          flexShrink: 0,
          padding: "12px 24px",
          borderTop: "1px solid #f0f0f0",
          backgroundColor: "#fff",
        }}
      >
        {/* Con el form siempre a la vista, Cancelar vuelve a lo guardado en vez de cerrar. */}
        <Button danger onClick={cargarConfiguracionGuardada}>
          {t("buttons.cancel")}
        </Button>
        <SaveButton
          {...saveButtonXlsProps}
          icon={<CheckOutlined />}
          iconPosition="end"
        >
          {t("buttons.save")}
        </SaveButton>
      </Flex>
    </Flex>
  );
};
